import { computed, inject, Injectable, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { PaginationSettings } from '#models';

@Injectable({ providedIn: 'root' })
export class PaginationService<TData> {
  private _translateService = inject(TranslateService);

  private _settings = signal<PaginationSettings<TData>>({
    totalCount: 0,
    pageNumber: 1,
    pageSize: 10,
    currentPage: 1,
    items: [],
  });

  currentPage = computed(() => this._settings().currentPage);
  pageNumber = computed(() => this._settings().pageNumber);
  pageSize = computed(() => this._settings().pageSize);
  totalCount = computed(() => this._settings().totalCount);
  data = computed(() => this._settings().items);

  currentRange = computed(() => {
    const s = this._settings();
    return this._translateService.instant('paginator.currentPageReport', {
      currentInitialRow: (s.pageNumber - 1) * s.pageSize + 1,
      currentLastRow: Math.min(s.pageNumber * s.pageSize, s.totalCount),
      totalRecords: s.totalCount,
    });
  });

  initialize(pageSize = 10) {
    this._settings.set({
      totalCount: 0,
      pageNumber: 1,
      currentPage: 1,
      items: [],
      pageSize,
    });
  }

  setPageNumber(page: number, first: number) {
    this._settings.update((v) => ({
      ...v,
      pageNumber: page,
      currentPage: first,
    }));
  }

  setPageSize(size: number) {
    this._settings.update((v) => ({
      ...v,
      pageSize: size,
      pageNumber: 1,
      currentPage: 1,
    }));
  }

  reset() {
    this._settings.update((v) => ({
      ...v,
      items: [],
      totalCount: 0,
      pageNumber: 1,
      currentPage: 1,
    }));
  }

  build(items: TData[], totalCount: number) {
    this._settings.update((v) => ({ ...v, items, totalCount }));
  }
}
